import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Briefcase,
  FolderKanban,
  FileText,
  Mail,
  MessageSquare,
  HelpCircle,
  Users,
  UserCheck,
  Award,
  CreditCard,
  LogOut,
  Menu,
  X,
  Brain,
  Settings,
  Globe,
  Share2,
  UserCog
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const userPermissions = user?.permissions || [];

  const allNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: null },
    { path: '/admin/site-settings', label: 'Site Ayarları', icon: Globe, permission: 'site_settings' },
    { path: '/admin/mail-settings', label: 'Mail Ayarları', icon: Mail, permission: 'mail_settings' },
    { path: '/admin/users', label: 'Kullanıcı Yönetimi', icon: UserCog, permission: 'users' },
    { path: '/admin/settings', label: 'Şifre Değiştir', icon: Settings, permission: null },
    { path: '/admin/technical-docs', label: 'AI Bilgi Bankası', icon: Brain, permission: 'technical_docs' },
    { path: '/admin/file-share', label: 'Dosya Paylaşımı', icon: Share2, permission: 'file_share' },
    { path: '/admin/payment-links', label: 'Ödeme Linkleri', icon: CreditCard, permission: 'payment_links' },
    { path: '/admin/services', label: 'Hizmetler', icon: Briefcase, permission: 'services' },
    { path: '/admin/projects', label: 'Projeler', icon: FolderKanban, permission: 'projects' },
    { path: '/admin/blog', label: 'Blog', icon: FileText, permission: 'blog' },
    { path: '/admin/certificates', label: 'Sertifikalar', icon: Award, permission: 'certificates' },
    { path: '/admin/jobs', label: 'Kariyer İlanları', icon: Users, permission: 'jobs' },
    { path: '/admin/applications', label: 'Başvurular', icon: UserCheck, permission: 'applications' },
    { path: '/admin/faq', label: 'SSS', icon: HelpCircle, permission: 'faq' },
    { path: '/admin/messages', label: 'Mesajlar', icon: Mail, permission: 'messages' },
    { path: '/admin/quotes', label: 'Teklifler', icon: MessageSquare, permission: 'quotes' },
  ];

  const navItems = allNavItems.filter(item =>
    item.permission === null || userPermissions.includes(item.permission)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Link to="/admin/dashboard" className="flex items-center">
          <img src="/ener-logo.png" alt="Ener Otomasyon Admin" className="h-10 w-auto" />
        </Link>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100"
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:block fixed top-0 left-0 z-40 w-64 h-screen bg-white border-r border-gray-200">
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <Link to="/admin/dashboard" className="flex items-center">
              <img src="/ener-logo.png" alt="Ener Otomasyon Admin" className="h-12 w-auto" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid={`admin-nav-${item.path.split('/').pop()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-gray-200">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={logout}
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {/* Sidebar - Mobile */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setIsMobileMenuOpen(false)}>
          <aside className="fixed top-0 left-0 bottom-0 w-72 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="h-full flex flex-col">
              {/* Logo */}
              <div className="p-6 border-b border-gray-200">
                <Link to="/admin/dashboard" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center">
                  <img src="/ener-logo.png" alt="Ener Otomasyon Admin" className="h-12 w-auto" />
                </Link>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`mobile-admin-nav-${item.path.split('/').pop()}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Logout */}
              <div className="p-4 border-t border-gray-200">
                <Button
                  variant="ghost"
                  className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    logout();
                    setIsMobileMenuOpen(false);
                  }}
                  data-testid="mobile-admin-logout"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Çıkış Yap
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64 pt-16 lg:pt-0">
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;